import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { resolveExerciseReference, getLatestPublicExercises, getVideoRanksForExercises, searchPublicExercises } from '../services/exerciseService'
import { getAllUserAttempts, getUserAttemptsInRange } from '../services/attemptService'
import { getEstablishments, getClasses } from '../services/referenceDataService'
import { updateUserProfile, updateBestDailyStreakEverIfHigher } from '../services/userService'
import { joinTeacherClass, leaveTeacherClass, getTeacherClassById, getTeacherClasses } from '../services/teacherClassService'
import { getAssignmentsByClass, getAssignmentsByTeacher } from '../services/assignmentService'
import { getActiveLiveSessionForClass, subscribeLiveSessionById } from '../services/liveSessionService'
import { revalidateStoredMarkerAnswer } from '../utils/exerciseMarkerValidation'
import FreeMode from './FreeMode'
import CampaignMap from '../components/CampaignMap'
import ParcoursNodePage from '../components/ParcoursNodePage'
import ActivityHeatmap from '../components/ActivityHeatmap'
import ExerciseSuggestions from '../components/ExerciseSuggestions'
import AchievementsDashboard from '../components/AchievementsDashboard'
import AttemptDetailModal from '../components/AttemptDetailModal'
import RequestEstablishmentModal from '../components/RequestEstablishmentModal'
import AvatarPickerModal from '../components/AvatarPickerModal'
import DailyLearningBlock from '../components/DailyLearningBlock'
import ExerciseCard from '../components/ExerciseCard'
import CodexPlaceholder from '../components/CodexPlaceholder'
import CodexView from '../components/CodexView'
import StudentBetaRestricted from '../components/StudentBetaRestricted'
import HorizonsMap from '../components/HorizonsMap'
import DiagnosticHero from '../components/DiagnosticHero'
import ParcoursMasteryCard from '../components/ParcoursMasteryCard'
import WeeklyActivityStrip from '../components/WeeklyActivityStrip'
import CompetencesSection from '../components/CompetencesSection'
import PracticePlan from '../components/PracticePlan'
import HomeworkPlan from '../components/HomeworkPlan'
import PreviewBanner from '../components/PreviewBanner'
import { getCodexUrlForEntry } from '../utils/codexContextLinks'
import { listPublishedCodexEntries } from '../services/codexService'
import { getAuthErrorMessage } from '../utils/errorHandler'
import { checkAndUnlockBadges, getUnlockedHorizonsStyles } from '../services/badgeService'
import { getUserProgress } from '../services/progressionService'
import { getPreviewProgress, getPreviewXP, getPreviewStreak, getPreviewBestStreakEver, getPreviewClassLabel } from '../utils/previewScenarios'
import { calculateStreak, calculateMaxStreakEver } from '../utils/dailyStreak'
import { countHorizonsExercisesByStyle, countsMeetMinimumForStyles, getHorizonsMinPublishedPerUnlockedStyle } from '../utils/horizonsCatalogGate'
import {
  buildDegreeAndCadenceStats,
  groupAttemptsByExercise,
  getStrongestAndWeakest,
  getPedagogicalSnapshot,
  buildDiagnosticCopy,
  getActionableWeakPoints,
  getCodexForWeakPoint,
  collectAutoTagsFromExercises,
  formatSuggestionsSubtitle,
  resolveActionableFilterTag
} from '../utils/studentProgressInsights'
import { buildParcoursProgressSummary } from '../utils/parcoursProgressSummary'
import './StudentDashboard.css'

const PREVIEW_SCENARIO_STORAGE_KEY = 'opus_preview_prof_scenario'
const PREVIEW_HORIZONS_STORAGE_KEY = 'opus_preview_horizons_override'
const PREVIEW_CLASS_STORAGE_KEY = 'opus_preview_prof_class'
/** Clé localStorage : l'élève a déjà ouvert la section Nouveaux Horizons → le conseil du jour repasse au tip normal */
const HORIZONS_SEEN_STORAGE_KEY = 'opus_horizons_panel_seen'
/** Nombre max d'entrées affichées dans la liste "Journal" (onglet Suivi). */
const PROGRESS_HISTORY_LIST_LIMIT = 10
/** Nombre d'entrées visibles par défaut avant "Voir plus". */
const PROGRESS_HISTORY_LIST_VISIBLE = 5
/** Bannière unlock Horizons déjà vue */
const HORIZONS_UNLOCK_SEEN_KEY = 'opus_horizons_unlock_banner_seen'
/** Nombre de devoirs affichés sur l'accueil avant "Voir plus" (évite la surcharge). */
const HOMEWORK_VISIBLE_INITIAL = 6
const DISABLE_DAILY_LEARNING_BLOCK = import.meta.env.VITE_DISABLE_DAILY_LEARNING_BLOCK === '1'

function StudentDashboard() {
  const {
    user,
    userData,
    logout,
    isGuest,
    disableGuestMode,
    refreshUserData,
    signInWithGoogle,
    loading: authLoading,
    isAdminTeacher,
    betaPrivateActive,
    hasFullCloudAccess,
  } = useAuth()
  const isPreviewMode = userData?.role === 'teacher'
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState('home')
  const [campaignSubTab, setCampaignSubTab] = useState('classic') // 'classic' | 'horizons'
  const [campaignSelectedNodeId, setCampaignSelectedNodeId] = useState(null) // page dédiée au nœud
  const [codexSelectedFicheId, setCodexSelectedFicheId] = useState(null)
  const [codexInitialNodeId, setCodexInitialNodeId] = useState(null)
  const [codexInitialFicheId, setCodexInitialFicheId] = useState(null)
  const [codexInitialSectionId, setCodexInitialSectionId] = useState(null)
  const [codexPublishedEntries, setCodexPublishedEntries] = useState([])
  const [codexLoading, setCodexLoading] = useState(false)
  /** Mode preview prof : scénario simulé ('' = données réelles du prof). Persisté en sessionStorage pour survivre à la navigation (ex. lancement / sortie d'un exercice). */
  const [previewScenario, setPreviewScenario] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem(PREVIEW_SCENARIO_STORAGE_KEY) ?? '') : ''
    } catch {
      return ''
    }
  })
  const [previewHorizonsOverride, setPreviewHorizonsOverride] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem(PREVIEW_HORIZONS_STORAGE_KEY) ?? '') : ''
    } catch {
      return ''
    }
  })
  /** Mode preview prof : classe sélectionnée pour afficher les devoirs ('' = aucune). Persisté en sessionStorage. */
  const [previewClassId, setPreviewClassId] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem(PREVIEW_CLASS_STORAGE_KEY) ?? '') : ''
    } catch {
      return ''
    }
  })
  const [previewTeacherClasses, setPreviewTeacherClasses] = useState([])
  const [exerciseCode, setExerciseCode] = useState('')
  const [codeError, setCodeError] = useState('')
  
  // États pour Suivi (ex-Progression)
  const [attempts, setAttempts] = useState([])
  const [loadingAttempts, setLoadingAttempts] = useState(true)
  const [selectedAttempt, setSelectedAttempt] = useState(null)
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [expandedJournalKey, setExpandedJournalKey] = useState(null)
  const [parcoursProgress, setParcoursProgress] = useState(null)
  const [loadingParcoursProgress, setLoadingParcoursProgress] = useState(false)
  const [showHorizonsUnlockBanner, setShowHorizonsUnlockBanner] = useState(false)
  const nextPracticeRef = useRef(null)
  
  // États pour Profil (édition inline nom)
  const [editingDisplayName, setEditingDisplayName] = useState(false)
  const [displayNameInput, setDisplayNameInput] = useState('')
  const [displayNameSaving, setDisplayNameSaving] = useState(false)
  const [displayNameError, setDisplayNameError] = useState(null)
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)

  // États pour Profil
  const [profileStats, setProfileStats] = useState(null)
  const [loadingProfileStats, setLoadingProfileStats] = useState(false)
  const [allAttempts, setAllAttempts] = useState([])
  const [establishmentsList, setEstablishmentsList] = useState([])
  const [classesList, setClassesList] = useState([])
  const [loadingEstablishments, setLoadingEstablishments] = useState(false)
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [profileEstablishment, setProfileEstablishment] = useState('')
  const [profileClass, setProfileClass] = useState('')
  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const [requestModalType, setRequestModalType] = useState('establishment')
  const [teacherClassName, setTeacherClassName] = useState(null)
  const [classCodeInput, setClassCodeInput] = useState('')
  const [joinClassLoading, setJoinClassLoading] = useState(false)
  const [joinClassError, setJoinClassError] = useState('')
  const [assignmentsForClass, setAssignmentsForClass] = useState([])
  const [attemptedExerciseIds, setAttemptedExerciseIds] = useState(new Set())
  const [attemptedAssignmentExerciseKeys, setAttemptedAssignmentExerciseKeys] = useState(new Set())
  const [loadingAssignments, setLoadingAssignments] = useState(false)
  const [homeworkFilter, setHomeworkFilter] = useState('all') // 'all' | 'new' | 'todo' | 'overdue' | 'done'

  const syncStudentDashboardUrl = useCallback((next) => {
    const params = new URLSearchParams(location.search)
    if (next.tab != null) {
      if (next.tab === 'home') params.delete('tab')
      else params.set('tab', next.tab)
      if (next.tab !== 'codex') {
        params.delete('fiche')
        params.delete('section')
      }
      if (next.tab !== 'campaign' && next.tab !== 'codex') {
        params.delete('node')
      }
      if (next.tab !== 'free-mode') {
        ;['q', 'composer', 'difficulty', 'tags', 'genre', 'formations', 'done', 'horizons', 'sort'].forEach((k) => params.delete(k))
      }
    }
    if (next.hw != null) {
      if (next.hw === 'all') params.delete('hw')
      else params.set('hw', next.hw)
    }
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
  }, [location.pathname, location.search, navigate])

  const setActiveTabAndUrl = useCallback((tab) => {
    setActiveTab(tab)
    syncStudentDashboardUrl({ tab })
  }, [syncStudentDashboardUrl])

  const [homeworkListExpanded, setHomeworkListExpanded] = useState(false) // true = afficher toute la liste sur l'accueil
  const [homeworkSecondaryFiltersOpen, setHomeworkSecondaryFiltersOpen] = useState(false)
  const [activeLiveSession, setActiveLiveSession] = useState(null)
  const [loadingLiveSession, setLoadingLiveSession] = useState(false)
  
  // État pour toutes les tentatives (pour le calcul du streak)
  const [allAttemptsForStreak, setAllAttemptsForStreak] = useState([])

  // Onglet précédent (pour retour depuis la page Profil, ouverte via avatar)
  const [previousTab, setPreviousTab] = useState('home')

  // Connexion Google depuis la carte invité (profil)
  const [isSigningInFromGuest, setIsSigningInFromGuest] = useState(false)

  // Objectifs de la semaine tous atteints (pour mise en avant suggestion prioritaire)
  const [allObjectivesCompleted, setAllObjectivesCompleted] = useState(false)

  // Derniers exercices ajoutés (accueil)
  const [latestExercises, setLatestExercises] = useState([])
  const [loadingLatestExercises, setLoadingLatestExercises] = useState(false)
  const [latestVideoRanksMap, setLatestVideoRanksMap] = useState(() => new Map())

  // Filtre initial pour le Mode Libre (clic sur une pastille → ouvrir Mode Libre avec ce filtre)
  const [freeModeInitialFilter, setFreeModeInitialFilter] = useState(null)
  /** Tags présents sur le catalogue Mode Libre (conseils de travail actionnables). */
  const [catalogTagSet, setCatalogTagSet] = useState(() => new Set())
  const [catalogTagsLoaded, setCatalogTagsLoaded] = useState(false)
  // Panneau Nouveaux Horizons (ouvert depuis le bouton Mode Libre ou depuis Progression, pas d’onglet dédié)
  const [hasSeenHorizonsPanel, setHasSeenHorizonsPanel] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem(HORIZONS_SEEN_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  /**
   * Navigation Nouveaux Horizons (sous-onglet Parcours) : pile de vues pour que "Retour" aille toujours au bon endroit.
   * Niveau 0 = accueil Horizons (cartes) ; niveau 1+ = style ouvert.
   * Retour = dépiler ; si plus qu’un seul niveau, quitter la page vers le dashboard.
   */
  const [horizonsViewStack, setHorizonsViewStack] = useState(() => ['cards'])

  /** Comptes publiés par style NH (élèves uniquement) ; null = pas encore chargé */
  const [horizonsCatalogCounts, setHorizonsCatalogCounts] = useState(null)
  const [horizonsCatalogError, setHorizonsCatalogError] = useState(false)

  // Marquer Horizons comme vu quand on ouvre le sous-onglet Horizons
  useEffect(() => {
    if (campaignSubTab === 'horizons') {
      try {
        window.localStorage.setItem(HORIZONS_SEEN_STORAGE_KEY, '1')
      } catch {}
      setHasSeenHorizonsPanel(true)
    }
  }, [campaignSubTab])

  // Réinitialiser la pile Horizons quand on entre dans le sous-onglet Horizons
  useEffect(() => {
    if (campaignSubTab === 'horizons') {
      setHorizonsViewStack(['cards'])
    }
  }, [campaignSubTab])

  // IDs des exercices déjà faits (pour filtre Mode Libre)
  const doneExerciseIds = useMemo(
    () => [...new Set((allAttemptsForStreak || []).map((a) => a.exerciseId).filter(Boolean))],
    [allAttemptsForStreak]
  )

  const journalGroups = useMemo(() => groupAttemptsByExercise(attempts || []), [attempts])

  // Ref pour scroll vers le parcours (onglet dédié)
  const campaignMapSectionRef = useRef(null)
  const dashboardAttemptsInitializedRef = useRef(false)

  // Petit écran (≤768px) pour limiter le graphique de progression
  const [isSmallScreen, setIsSmallScreen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const handler = () => setIsSmallScreen(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])



  useEffect(() => {
    if (location.pathname === '/student-dashboard' && location.state?.tab) {
      setActiveTab(location.state.tab)
    }
  }, [location.pathname, location.state?.tab])

  // URL : tab=codex|campaign|free-mode|home, fiche, node, section, hw (filtre devoirs)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    const fiche = params.get('fiche')
    const node = params.get('node')
    const section = params.get('section')
    const hw = params.get('hw')
    if (tab === 'codex') {
      setActiveTab('codex')
      if (fiche) setCodexInitialFicheId(fiche)
      if (node) setCodexInitialNodeId(node)
      if (section) setCodexInitialSectionId(section)
      else setCodexInitialSectionId(null)
    } else if (tab === 'campaign' && node) {
      setActiveTab('campaign')
      setCampaignSubTab('classic')
      setCampaignSelectedNodeId(node)
    } else if (tab === 'free-mode') {
      setActiveTab('free-mode')
    } else if (tab === 'home' || tab === 'progress' || tab === 'profile') {
      setActiveTab(tab)
    }
    if (hw && ['all', 'new', 'todo', 'overdue', 'done'].includes(hw)) {
      setHomeworkFilter(hw)
    }
  }, [location.search])

  const useTeaserCatalogOnly = Boolean(
    betaPrivateActive && !hasFullCloudAccess && !isPreviewMode
  )

  useEffect(() => {
    setCodexLoading(true)
    listPublishedCodexEntries({ publicTeaserOnly: useTeaserCatalogOnly })
      .then((entries) => setCodexPublishedEntries(entries))
      .catch(() => setCodexPublishedEntries([]))
      .finally(() => setCodexLoading(false))
  }, [useTeaserCatalogOnly])

  // Charger l'historique des tentatives (uniquement si connecté, pas en mode invité)
  useEffect(() => {
    if (user && !isGuest) {
      loadAttempts()
      loadAllAttemptsForStreak()
      dashboardAttemptsInitializedRef.current = true
    } else {
      setLoadingAttempts(false)
    }
  }, [user, isGuest])
  
  // Recharger les tentatives quand on revient au dashboard (pour mettre à jour le streak)
  useEffect(() => {
    if (!dashboardAttemptsInitializedRef.current) return
    if (user && !isGuest && location.pathname === '/student-dashboard') {
      loadAttempts()
      loadAllAttemptsForStreak()
    }
  }, [location.pathname, user, isGuest])
  
  // Fonction pour charger toutes les tentatives pour le calcul du streak
  const loadAllAttemptsForStreak = async () => {
    if (!user || isGuest) return
    try {
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      const data = await getUserAttemptsInRange(user.uid, {
        startDate: oneYearAgo,
        endDate: new Date(),
        maxCount: 800
      })
      setAllAttemptsForStreak(data)
    } catch (error) {
      console.error('Erreur lors du chargement de toutes les tentatives:', error)
    }
  }

  // Charger toutes les tentatives pour les stats du profil
  useEffect(() => {
    if (user && !isGuest && activeTab === 'profile') {
      loadProfileStats()
    }
  }, [user, isGuest, activeTab])

  // Charger listes établissements / classes quand on ouvre le profil (uniquement si connecté, pas en mode invité)
  useEffect(() => {
    if (activeTab !== 'profile' || isGuest) return
    setLoadingEstablishments(true)
    setLoadingClasses(true)
    getEstablishments()
      .then((list) => setEstablishmentsList(list))
      .catch(() => setEstablishmentsList([]))
      .finally(() => setLoadingEstablishments(false))
    getClasses()
      .then((list) => setClassesList(list))
      .catch(() => setClassesList([]))
      .finally(() => setLoadingClasses(false))
  }, [activeTab, isGuest])

  // Synchroniser établissement/classe depuis userData à l'ouverture du profil
  useEffect(() => {
    if (activeTab === 'profile' && userData) {
      setProfileEstablishment(userData.establishment ?? '')
      setProfileClass(userData.class ?? '')
    }
  }, [activeTab, userData?.establishment, userData?.class])

  // Charger le nom de la classe prof quand l'élève a rejoint une classe
  useEffect(() => {
    if (!userData?.teacherClassId) {
      setTeacherClassName(null)
      return
    }
    getTeacherClassById(userData.teacherClassId)
      .then((tc) => setTeacherClassName(tc?.name ?? null))
      .catch(() => setTeacherClassName(null))
  }, [userData?.teacherClassId])

  // Charger les classes du prof en mode preview (pour le sélecteur "Classe (preview)")
  useEffect(() => {
    if (!user?.uid || userData?.role !== 'teacher') {
      setPreviewTeacherClasses([])
      return
    }
    getTeacherClasses(user.uid)
      .then((list) => setPreviewTeacherClasses(list || []))
      .catch(() => setPreviewTeacherClasses([]))
  }, [user?.uid, userData?.role])

  // Classe effective pour les devoirs : élève = teacherClassId ; prof en preview = previewClassId
  const effectiveClassIdForAssignments =
    userData?.role === 'teacher' && previewClassId
      ? previewClassId
      : userData?.teacherClassId

  // Session live active (lookup léger puis écoute par ID pour éviter les oscillations en preview prof).
  useEffect(() => {
    if (!effectiveClassIdForAssignments || isGuest || activeTab !== 'home') {
      setActiveLiveSession(null)
      return undefined
    }
    let unsubscribe = null
    let cancelled = false
    setLoadingLiveSession(true)

    const lookupTeacherId = userData?.role === 'teacher' ? user?.uid : null
    getActiveLiveSessionForClass(effectiveClassIdForAssignments, {
      teacherId: lookupTeacherId || undefined
    })
      .then((session) => {
        if (cancelled) return
        setActiveLiveSession(session || null)
        if (session) {
          unsubscribe = subscribeLiveSessionById(
            session.id,
            (next) => setActiveLiveSession(next || null),
            () => {}
          )
        }
      })
      .catch((error) => {
        console.error('StudentDashboard live lookup failed:', {
          classId: effectiveClassIdForAssignments,
          code: error?.code,
          message: error?.message
        })
      })
      .finally(() => {
        if (!cancelled) setLoadingLiveSession(false)
      })

    return () => {
      cancelled = true
      if (unsubscribe) unsubscribe()
    }
  }, [effectiveClassIdForAssignments, isGuest, activeTab])

  // Charger les devoirs de la classe + tentatives (pour Fait/À faire) — 2 requêtes, limitées
  // En mode preview prof : on charge via getAssignmentsByTeacher puis filtre par classe (même source que la page Devoirs).
  useEffect(() => {
    if (!user?.uid || !effectiveClassIdForAssignments || isGuest) {
      setAssignmentsForClass([])
      setAttemptedExerciseIds(new Set())
      setAttemptedAssignmentExerciseKeys(new Set())
      return
    }
    setLoadingAssignments(true)
    const isPreviewByClass = userData?.role === 'teacher' && previewClassId === effectiveClassIdForAssignments
    const loadAssignments = isPreviewByClass
      ? getAssignmentsByTeacher(user.uid, 30).then((list) =>
          (list || []).filter((a) => a.teacherClassId === effectiveClassIdForAssignments)
        )
      : getAssignmentsByClass(effectiveClassIdForAssignments, 30)
    const assignmentWindowStart = new Date()
    assignmentWindowStart.setMonth(assignmentWindowStart.getMonth() - 6)
    Promise.all([
      loadAssignments,
      getUserAttemptsInRange(user.uid, {
        startDate: assignmentWindowStart,
        endDate: new Date(),
        maxCount: 1200
      })
    ])
      .then(([assignments, attempts]) => {
        setAssignmentsForClass(assignments || [])
        const ids = new Set((attempts || []).map((a) => a.exerciseId).filter(Boolean))
        const assignmentKeys = new Set(
          (attempts || [])
            .filter((a) => a.assignmentId && a.exerciseId)
            .map((a) => `${a.assignmentId}:${a.exerciseId}`)
        )
        setAttemptedExerciseIds(ids)
        setAttemptedAssignmentExerciseKeys(assignmentKeys)
      })
      .catch(() => {
        setAssignmentsForClass([])
        setAttemptedExerciseIds(new Set())
        setAttemptedAssignmentExerciseKeys(new Set())
      })
      .finally(() => setLoadingAssignments(false))
  }, [user?.uid, effectiveClassIdForAssignments, isGuest])

  // Charger stats + progression parcours pour l’onglet Suivi
  useEffect(() => {
    if (user && !isGuest && activeTab === 'progress') {
      if (!profileStats) loadProfileStats()
      loadParcoursProgress()
    }
  }, [user, isGuest, activeTab])

  const loadParcoursProgress = async () => {
    if (!user || isGuest) return
    try {
      setLoadingParcoursProgress(true)
      if (isPreviewMode && previewScenario) {
        const preview = getPreviewProgress(previewScenario)
        setParcoursProgress(preview || null)
      } else {
        const progress = await getUserProgress(user.uid)
        setParcoursProgress(progress)
      }
    } catch (error) {
      console.error('Erreur chargement progression parcours:', error)
      setParcoursProgress(null)
    } finally {
      setLoadingParcoursProgress(false)
    }
  }

  // Charger les derniers exercices sur l'accueil
  useEffect(() => {
    if (activeTab !== 'home') return
    let cancelled = false
    setLoadingLatestExercises(true)
    getLatestPublicExercises(6, { publicTeaserOnly: useTeaserCatalogOnly })
      .then((data) => {
        if (!cancelled) setLatestExercises(data)
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Erreur chargement derniers exercices:', err)
          setLatestExercises([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLatestExercises(false)
      })
    return () => { cancelled = true }
  }, [activeTab, useTeaserCatalogOnly])

  useEffect(() => {
    if (!latestExercises.length) {
      setLatestVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(latestExercises).then(setLatestVideoRanksMap).catch(() => setLatestVideoRanksMap(new Map()))
  }, [latestExercises])

  // Catalogue de tags Mode Libre (pour n’offrir que des conseils de travail actionnables)
  useEffect(() => {
    let cancelled = false
    setCatalogTagsLoaded(false)
    searchPublicExercises({
      excludeHorizons: true,
      ...(useTeaserCatalogOnly ? { publicTeaserOnly: true } : {})
    })
      .then((exercises) => {
        if (!cancelled) {
          setCatalogTagSet(collectAutoTagsFromExercises(exercises))
          setCatalogTagsLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogTagSet(new Set())
          setCatalogTagsLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [useTeaserCatalogOnly])

  const loadAttempts = async () => {
    try {
      setLoadingAttempts(true)
      // Charger une fenêtre large mais bornée pour limiter le quota Firestore
      const startDate = new Date()
      startDate.setMonth(startDate.getMonth() - 6)
      const data = await getUserAttemptsInRange(user.uid, {
        startDate,
        endDate: new Date(),
        maxCount: 800
      })
      setAttempts(data)
      
      // Vérifier et débloquer les badges
      if (data.length > 0) {
        try {
          const newlyUnlocked = await checkAndUnlockBadges(user.uid, data, { xp: userData?.xp })
          if (newlyUnlocked.length > 0) {
            console.log('Nouveaux badges débloqués:', newlyUnlocked)
          }
        } catch (error) {
          console.error('Erreur lors de la vérification des badges:', error)
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des tentatives:', error)
    } finally {
      setLoadingAttempts(false)
    }
  }

  const handleCodeSubmit = async (e) => {
    e.preventDefault()
    setCodeError('')
    
    if (!exerciseCode.trim()) {
      setCodeError('Veuillez entrer un code d\'exercice')
      return
    }
    
    try {
      const exercise = await resolveExerciseReference(exerciseCode.trim())
      
      if (!exercise) {
        setCodeError('Exercice introuvable. Vérifiez le code.')
        return
      }
      
      if (exercise.status !== 'published' && exercise.status !== 'draft') {
        setCodeError('Cet exercice n\'est pas accessible')
        return
      }
      
      navigate(`/play/${exercise.id}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })
    } catch (error) {
      console.error('Erreur lors de la recherche:', error)
      if (error?.code === 'permission-denied') {
        setCodeError('Cet exercice n’est pas accessible avec votre compte (beta privée).')
      } else {
        setCodeError('Erreur lors de la recherche de l\'exercice')
      }
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short'
    }).format(date)
  }

  const homeworkRows = useMemo(() => {
    const now = Date.now()
    return (assignmentsForClass || []).map((assignment) => {
      const items = Array.isArray(assignment.items) && assignment.items.length > 0
        ? assignment.items
        : [{ exerciseId: assignment.exerciseId, exerciseTitle: assignment.exerciseTitle || 'Exercice' }]

      const doneCount = items.filter((item) => {
        if (!item?.exerciseId) return false
        const linkedKey = assignment.id ? `${assignment.id}:${item.exerciseId}` : null
        if (linkedKey && attemptedAssignmentExerciseKeys.has(linkedKey)) return true
        // rétrocompatibilité : anciennes tentatives sans assignmentId
        return attemptedExerciseIds.has(item.exerciseId)
      }).length
      const totalCount = items.length
      const isDone = totalCount > 0 && doneCount >= totalCount

      const dueDate = assignment.dueDate ? (assignment.dueDate.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate)) : null
      const isOverdue = !!dueDate && dueDate.getTime() < now && !isDone
      const createdAt = assignment.createdAt ? (assignment.createdAt.toDate ? assignment.createdAt.toDate() : new Date(assignment.createdAt)) : null
      const isNew = !!createdAt && (now - createdAt.getTime()) <= (72 * 60 * 60 * 1000) && !isDone
      const firstTodo = items.find((item) => {
        if (!item?.exerciseId) return false
        const linkedKey = assignment.id ? `${assignment.id}:${item.exerciseId}` : null
        if (linkedKey && attemptedAssignmentExerciseKeys.has(linkedKey)) return false
        return !attemptedExerciseIds.has(item.exerciseId)
      })
      const targetExerciseId = firstTodo?.exerciseId || items[0]?.exerciseId || assignment.exerciseId

      let status = 'todo'
      if (isDone) status = 'done'
      else if (isOverdue) status = 'overdue'
      else if (isNew) status = 'new'

      return {
        ...assignment,
        items,
        doneCount,
        totalCount,
        isDone,
        isOverdue,
        isNew,
        status,
        targetExerciseId,
        dueDate
      }
    })
  }, [assignmentsForClass, attemptedAssignmentExerciseKeys, attemptedExerciseIds])

  const filteredHomeworkRows = useMemo(() => {
    if (homeworkFilter === 'all') return homeworkRows
    return homeworkRows.filter((row) => row.status === homeworkFilter || (homeworkFilter === 'todo' && row.status === 'new'))
  }, [homeworkFilter, homeworkRows])

  const homeworkCounts = useMemo(() => {
    return homeworkRows.reduce((acc, row) => {
      acc.all += 1
      if (row.status === 'new') acc.new += 1
      if (row.status === 'overdue') acc.overdue += 1
      if (row.status === 'done') acc.done += 1
      if (row.status === 'todo' || row.status === 'new') acc.todo += 1
      return acc
    }, { all: 0, new: 0, overdue: 0, todo: 0, done: 0 })
  }, [homeworkRows])

  const homeworkPrimaryFilters = useMemo(() => ([
    { key: 'all', label: 'Tous', count: homeworkCounts.all },
    { key: 'todo', label: 'À faire', count: homeworkCounts.todo },
    { key: 'done', label: 'Faits', count: homeworkCounts.done }
  ]), [homeworkCounts])

  const homeworkSecondaryFilters = useMemo(() => ([
    { key: 'new', label: 'Nouveaux', count: homeworkCounts.new },
    { key: 'overdue', label: 'En retard', count: homeworkCounts.overdue }
  ]), [homeworkCounts])

  const shouldShowHomeworkSecondaryToggle = useMemo(() => {
    return homeworkSecondaryFilters.some((filter) => filter.count > 0 || homeworkFilter === filter.key)
  }, [homeworkSecondaryFilters, homeworkFilter])

  useEffect(() => {
    if (!shouldShowHomeworkSecondaryToggle && homeworkSecondaryFiltersOpen) {
      setHomeworkSecondaryFiltersOpen(false)
    }
  }, [shouldShowHomeworkSecondaryToggle, homeworkSecondaryFiltersOpen])

  // Fonction pour nettoyer le titre en enlevant le préfixe "Analyse harmonique - "
  const cleanExerciseTitle = (title) => {
    if (!title) return 'Exercice'
    // Enlever le préfixe "Analyse harmonique - " s'il existe (insensible à la casse)
    const cleaned = title.replace(/^Analyse harmonique\s*-\s*/i, '').trim()
    return cleaned || 'Exercice'
  }

  const usePreviewValues = isPreviewMode && previewScenario && getPreviewXP(previewScenario) != null
  const effectiveXp = usePreviewValues ? getPreviewXP(previewScenario) : (userData?.xp ?? 0)

  // Calcul centralisé : Nouveaux Horizons débloqué ?
  const horizonsUnlockedStyles = useMemo(() => {
    if (isPreviewMode) {
      return previewHorizonsOverride
        ? previewHorizonsOverride.split(',').map(s => s.trim()).filter(Boolean)
        : []
    }
    return getUnlockedHorizonsStyles(allAttemptsForStreak || [], { xp: effectiveXp })
  }, [isPreviewMode, previewHorizonsOverride, allAttemptsForStreak, effectiveXp])
  const horizonsUnlockedCount = horizonsUnlockedStyles.length
  const horizonsUnlocked = horizonsUnlockedCount > 0

  // Catalogue publié Horizons (élèves connectés — pas preview prof) pour masquer l’UI si trop peu d’exercices par style débloqué
  useEffect(() => {
    if (!user?.uid || isGuest || isPreviewMode) {
      setHorizonsCatalogCounts(null)
      setHorizonsCatalogError(false)
      return
    }
    let cancelled = false
    setHorizonsCatalogError(false)
    searchPublicExercises({
      onlyHorizons: true,
      ...(useTeaserCatalogOnly ? { publicTeaserOnly: true } : {}),
    })
      .then((list) => {
        if (cancelled) return
        setHorizonsCatalogCounts(countHorizonsExercisesByStyle(list))
      })
      .catch(() => {
        if (cancelled) return
        setHorizonsCatalogError(true)
        setHorizonsCatalogCounts(null)
      })
    return () => { cancelled = true }
  }, [user?.uid, isGuest, isPreviewMode, useTeaserCatalogOnly])

  const showHorizonsUi = useMemo(() => {
    if (isPreviewMode) return horizonsUnlocked
    if (!horizonsUnlocked) return false
    if (horizonsCatalogCounts == null || horizonsCatalogError) return false
    const min = getHorizonsMinPublishedPerUnlockedStyle()
    return countsMeetMinimumForStyles(horizonsUnlockedStyles, horizonsCatalogCounts, min)
  }, [
    isPreviewMode,
    horizonsUnlocked,
    horizonsUnlockedStyles,
    horizonsCatalogCounts,
    horizonsCatalogError
  ])

  // Si l’UI Nouveaux Horizons n’est pas affichée, forcer retour au sous-onglet Parcours classique
  useEffect(() => {
    if (!showHorizonsUi && campaignSubTab === 'horizons') {
      setCampaignSubTab('classic')
    }
  }, [showHorizonsUi, campaignSubTab])

  const userName = userData?.displayName || user?.displayName || 'Élève'
  const firstName = userName.split(' ')[0]
  const profilePhotoURL = userData?.photoURL ?? user?.photoURL
  const streak = usePreviewValues ? getPreviewStreak(previewScenario) : calculateStreak(allAttemptsForStreak)
  const bestFromAttempts = useMemo(
    () => calculateMaxStreakEver(allAttemptsForStreak || []),
    [allAttemptsForStreak]
  )
  const bestStreakEverDisplay = useMemo(() => {
    if (usePreviewValues) {
      return Math.max(0, getPreviewBestStreakEver(previewScenario) ?? 0)
    }
    return Math.max(
      Math.max(0, Math.floor(Number(userData?.bestDailyStreakEver ?? 0))),
      bestFromAttempts
    )
  }, [usePreviewValues, previewScenario, userData?.bestDailyStreakEver, bestFromAttempts])

  useEffect(() => {
    if (!user?.uid || isGuest || usePreviewValues) return
    const fromAtt = calculateMaxStreakEver(allAttemptsForStreak || [])
    const candidate = Math.max(fromAtt, streak)
    const stored = Math.max(0, Math.floor(Number(userData?.bestDailyStreakEver ?? 0)))
    if (candidate <= stored) return
    updateBestDailyStreakEverIfHigher(user.uid, candidate).catch((e) =>
      console.warn('bestDailyStreakEver:', e)
    )
  }, [
    user?.uid,
    isGuest,
    usePreviewValues,
    allAttemptsForStreak,
    streak,
    userData?.bestDailyStreakEver
  ])

  const scenarioClassLabel = previewScenario ? getPreviewClassLabel(previewScenario) : null

  const startEditingDisplayName = () => {
    setDisplayNameInput(userData?.displayName || user?.displayName || '')
    setDisplayNameError(null)
    setEditingDisplayName(true)
  }

  const cancelEditingDisplayName = () => {
    setEditingDisplayName(false)
    setDisplayNameInput('')
    setDisplayNameError(null)
  }

  const saveDisplayName = async () => {
    const trimmed = displayNameInput?.trim() || ''
    if (!user?.uid) return
    setDisplayNameError(null)
    if (!trimmed) {
      setDisplayNameError('Le nom ne peut pas être vide.')
      return
    }
    setDisplayNameSaving(true)
    try {
      await updateUserProfile(user.uid, { displayName: trimmed })
      await refreshUserData()
      setEditingDisplayName(false)
      setDisplayNameInput('')
    } catch (err) {
      setDisplayNameError(err.message || 'Erreur lors de l\'enregistrement.')
    } finally {
      setDisplayNameSaving(false)
    }
  }

  // Micro-animation série : une seule pulsation au chargement quand streak > 0
  const [streakAnimate, setStreakAnimate] = useState(false)
  const streakAnimateDoneRef = useRef(false)
  useEffect(() => {
    if (isGuest || streak === 0 || streakAnimateDoneRef.current) return
    setStreakAnimate(true)
    const t = setTimeout(() => {
      setStreakAnimate(false)
      streakAnimateDoneRef.current = true
    }, 1300)
    return () => clearTimeout(t)
  }, [streak, isGuest])

  const [streakTooltipOpen, setStreakTooltipOpen] = useState(false)
  const streakWrapRef = useRef(null)

  useEffect(() => {
    if (!streakTooltipOpen) return
    const onDocMouseDown = (e) => {
      if (streakWrapRef.current && !streakWrapRef.current.contains(e.target)) {
        setStreakTooltipOpen(false)
      }
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setStreakTooltipOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [streakTooltipOpen])

  const handleHeaderCtaClick = () => {
    setActiveTabAndUrl('campaign')
  }

  const joinActiveLiveSession = () => {
    if (!activeLiveSession) return
    const queue = Array.isArray(activeLiveSession.exerciseQueue) ? activeLiveSession.exerciseQueue : []
    const currentIndex = Number(activeLiveSession.currentExerciseIndex || 0)
    const current = queue[currentIndex]
    if (!current?.exerciseId) return
    navigate(`/play/${current.exerciseId}`, {
      state: {
        liveSessionId: activeLiveSession.id,
        liveCorrectionReleased: activeLiveSession.status === 'correction_released',
        returnTo: `${location.pathname}${location.search || ''}`
      }
    })
  }

  // Charger les statistiques du profil
  const loadProfileStats = async () => {
    if (!user || isGuest) return
    
    try {
      setLoadingProfileStats(true)
      const attemptsData = await getAllUserAttempts(user.uid)
      setAllAttempts(attemptsData)
      setProfileStats(buildDegreeAndCadenceStats(attemptsData))
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error)
    } finally {
      setLoadingProfileStats(false)
    }
  }

  // Calculer les données pour le radar
  const radarData = useMemo(() => {
    if (!profileStats || !allAttempts || allAttempts.length === 0) {
      return []
    }

    // Paramètres configurables
    const MIN_PRACTICE_ATTEMPTS = 10 // Seuil minimum de tentatives pour pratique suffisante
    const HIGH_ACCURACY_THRESHOLD = 90 // Seuil de précision élevée (en %)
    const ACCURACY_WEIGHT = 0.6 // Poids de la précision (60%)
    const PRACTICE_WEIGHT = 0.4 // Poids de la pratique (40%)

    // Fonction pour calculer le score de pratique basé sur le nombre de tentatives
    const calculatePracticeScore = (attemptCount) => {
      if (attemptCount === 0) return 0
      // Fonction sigmoïde : atteint ~95% à MIN_PRACTICE_ATTEMPTS, 100% à 2*MIN_PRACTICE_ATTEMPTS
      const normalized = Math.min(100, (attemptCount / MIN_PRACTICE_ATTEMPTS) * 95)
      return Math.round(normalized)
    }

    // Fonction pour calculer le score de précision avec réponses partielles
    const calculateAccuracyScore = (validations) => {
      if (!validations || validations.length === 0) return 0
      
      // Calculer la moyenne pondérée des scores
      // Les scores sont déjà en pourcentage (0-100)
      const totalScore = validations.reduce((sum, v) => sum + (v.score || 0), 0)
      const averageScore = totalScore / validations.length
      
      return Math.round(averageScore)
    }

    // Fonction pour combiner précision et pratique
    const combineScores = (accuracyScore, practiceScore) => {
      const combined = (accuracyScore * ACCURACY_WEIGHT) + (practiceScore * PRACTICE_WEIGHT)
      return Math.round(Math.min(100, combined))
    }

    // Collecter toutes les validations par catégorie
    const tonicValidations = []
    const subdominantValidations = []
    const dominantValidations = []
    const cadenceValidations = []
    const borrowedValidations = []

    // Compteurs de tentatives par catégorie
    const tonicAttempts = new Set()
    const subdominantAttempts = new Set()
    const dominantAttempts = new Set()
    const cadenceAttempts = new Set()
    const borrowedAttempts = new Set()

    // Parcourir toutes les tentatives pour recalculer les validations
    allAttempts.forEach((attempt) => {
      if (!attempt.correctAnswers || !Array.isArray(attempt.correctAnswers)) return

      attempt.correctAnswers.forEach((correct, index) => {
        if (!correct) return

        const userAnswer = attempt.userAnswers?.[index]
        if (!userAnswer) return

        const validation = revalidateStoredMarkerAnswer(userAnswer, correct, {
          index,
          progressionMode: attempt.progressionMode,
          tonality: attempt.tonality,
          mode: attempt.mode || attempt.dominantMode
        })

        // Classer par catégorie selon le degré (normaliser en majuscules pour la comparaison)
        const degree = (correct.degree || '').toUpperCase()
        
        // Tonique (T) : I, III, VI
        if (degree === 'I' || degree === 'III' || degree === 'VI') {
          tonicValidations.push(validation)
          tonicAttempts.add(attempt.id)
        }

        // Sous-dominante (SD) : IV, II
        if (degree === 'IV' || degree === 'II') {
          subdominantValidations.push(validation)
          subdominantAttempts.add(attempt.id)
        }

        // Dominante (D) : V, VII°
        if (degree === 'V' || degree === 'VII°' || degree === 'VII') {
          dominantValidations.push(validation)
          dominantAttempts.add(attempt.id)
        }

        // Stats par cadence
        const cadence = correct.cadence || ''
        if (cadence) {
          cadenceValidations.push(validation)
          cadenceAttempts.add(attempt.id)
        }

        // Stats pour les emprunts (isBorrowed === true)
        if (correct.isBorrowed === true) {
          borrowedValidations.push(validation)
          borrowedAttempts.add(attempt.id)
        }
      })
    })

    // Calculer les scores pour chaque catégorie
    const tonicAccuracy = calculateAccuracyScore(tonicValidations)
    const tonicPractice = calculatePracticeScore(tonicAttempts.size)
    const tonicValue = combineScores(tonicAccuracy, tonicPractice)

    const subdominantAccuracy = calculateAccuracyScore(subdominantValidations)
    const subdominantPractice = calculatePracticeScore(subdominantAttempts.size)
    const subdominantValue = combineScores(subdominantAccuracy, subdominantPractice)

    const dominantAccuracy = calculateAccuracyScore(dominantValidations)
    const dominantPractice = calculatePracticeScore(dominantAttempts.size)
    const dominantValue = combineScores(dominantAccuracy, dominantPractice)

    const cadenceAccuracy = calculateAccuracyScore(cadenceValidations)
    const cadencePractice = calculatePracticeScore(cadenceAttempts.size)
    const cadenceValue = combineScores(cadenceAccuracy, cadencePractice)

    const borrowedAccuracy = calculateAccuracyScore(borrowedValidations)
    const borrowedPractice = calculatePracticeScore(borrowedAttempts.size)
    const borrowedValue = combineScores(borrowedAccuracy, borrowedPractice)

    const result = [
      { axis: 'Tonique', value: tonicValue },
      { axis: 'Sous-Dom.', value: subdominantValue },
      { axis: 'Dominante', value: dominantValue },
      { axis: 'Cadences', value: cadenceValue },
      { axis: 'Emprunts', value: borrowedValue }
    ]
    
    return result
  }, [profileStats, allAttempts])

  const tagsForWorkAdvice = catalogTagsLoaded ? catalogTagSet : null

  const actionableWeakPoints = useMemo(
    () =>
      tagsForWorkAdvice
        ? getActionableWeakPoints(
            profileStats,
            allAttemptsForStreak,
            tagsForWorkAdvice,
            { max: 3 }
          )
        : [],
    [profileStats, allAttemptsForStreak, tagsForWorkAdvice]
  )

  const { strongestPoint } = useMemo(
    () => getStrongestAndWeakest(profileStats),
    [profileStats]
  )

  const weakSkillsList = actionableWeakPoints

  const parcoursSummary = useMemo(
    () => buildParcoursProgressSummary(parcoursProgress),
    [parcoursProgress]
  )

  const pendingHomeworkCount = useMemo(() => {
    return (homeworkRows || []).filter(
      (r) => r.status === 'todo' || r.status === 'new' || r.status === 'overdue'
    ).length
  }, [homeworkRows])

  const diagnosticCopy = useMemo(() => {
    const pedagogical = getPedagogicalSnapshot(allAttemptsForStreak || [], 50)
    return buildDiagnosticCopy({
      strongest: strongestPoint,
      priorities: actionableWeakPoints,
      pedagogical,
      activeNode: parcoursSummary.activeNodeId
        ? {
            id: parcoursSummary.activeNodeId,
            label: parcoursSummary.activeNodeLabel,
            modeLabel: parcoursSummary.modeLabel,
            masteryLabel: parcoursSummary.masteryLabel
          }
        : null,
      hasHomework: pendingHomeworkCount > 0,
      homeworkCount: pendingHomeworkCount
    })
  }, [
    strongestPoint,
    actionableWeakPoints,
    allAttemptsForStreak,
    parcoursSummary,
    pendingHomeworkCount
  ])

  // Bannière unlock Horizons (une fois)
  useEffect(() => {
    if (isGuest || !showHorizonsUi) {
      setShowHorizonsUnlockBanner(false)
      return
    }
    try {
      const seen = localStorage.getItem(HORIZONS_UNLOCK_SEEN_KEY) === '1'
      if (!seen && horizonsUnlockedCount > 0) {
        setShowHorizonsUnlockBanner(true)
      }
    } catch {
      /* ignore */
    }
  }, [isGuest, showHorizonsUi, horizonsUnlockedCount])

  const dismissHorizonsUnlockBanner = () => {
    setShowHorizonsUnlockBanner(false)
    try {
      localStorage.setItem(HORIZONS_UNLOCK_SEEN_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const scrollToNextPractice = useCallback(() => {
    nextPracticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleWorkWeakSkill = useCallback(
    (point) => {
      if (!point) {
        scrollToNextPractice()
        return
      }
      const filterTag =
        point.filterTag || resolveActionableFilterTag(point, catalogTagSet)
      if (filterTag) {
        setFreeModeInitialFilter({ tag: filterTag })
        setActiveTabAndUrl('free-mode')
        return
      }
      const codex = getCodexForWeakPoint(point)
      if (codex?.ficheId) {
        setActiveTabAndUrl('codex')
        setCodexInitialFicheId(codex.ficheId)
        setCodexInitialSectionId(codex.sectionId || null)
        return
      }
      scrollToNextPractice()
    },
    [scrollToNextPractice, catalogTagSet]
  )

  const handleDiagnosticPrimary = useCallback(() => {
    const action = diagnosticCopy.primaryAction
    if (action === 'homework') {
      document.getElementById('suivi-homework')?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    if (action === 'weak_skill') {
      handleWorkWeakSkill(weakSkillsList[0])
      return
    }
    if (action === 'parcours') {
      setActiveTabAndUrl('campaign')
      setCampaignSubTab('classic')
      return
    }
    if (action === 'suggestions' || action === 'competences') {
      scrollToNextPractice()
      return
    }
    setActiveTab('home')
  }, [diagnosticCopy, handleWorkWeakSkill, weakSkillsList, scrollToNextPractice])

  const handleDiagnosticSecondary = useCallback(() => {
    const action = diagnosticCopy.secondaryAction
    if (action === 'parcours') {
      setActiveTabAndUrl('campaign')
      setCampaignSubTab('classic')
      return
    }
    if (action === 'suggestions' || action === 'competences') {
      scrollToNextPractice()
      return
    }
    if (action === 'homework') {
      document.getElementById('suivi-homework')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [diagnosticCopy, scrollToNextPractice])

  const handleHorizonsBack = () => {
    if (horizonsViewStack.length > 1) {
      setHorizonsViewStack((prev) => prev.slice(0, -1))
    } else {
      setCampaignSubTab('classic')
    }
  }

  /** Dérivé : style actuellement ouvert (null = on est sur l’accueil Horizons) */
  const selectedHorizonsStyleId =
    horizonsViewStack.length > 1 && horizonsViewStack[horizonsViewStack.length - 1] !== 'cards'
      ? horizonsViewStack[horizonsViewStack.length - 1]
      : null

  const setSelectedHorizonsStyleId = (styleIdOrNull) => {
    if (styleIdOrNull == null) {
      setHorizonsViewStack(['cards'])
    } else {
      setHorizonsViewStack((prev) => [...prev, styleIdOrNull])
    }
  }

  if (user && !isGuest && userData?.role === 'student' && betaPrivateActive && !hasFullCloudAccess) {
    return <StudentBetaRestricted />
  }

  return (
    <div className={`student-dashboard${isPreviewMode ? ' student-dashboard--preview' : ''}`}>
      {/* Bannière Preview pour les professeurs (y compris sur la page Nouveaux Horizons) */}
      {isPreviewMode && (
        <PreviewBanner
          previewScenario={previewScenario}
          previewHorizonsOverride={previewHorizonsOverride}
          previewClassId={previewClassId}
          previewTeacherClasses={previewTeacherClasses}
          scenarioClassLabel={scenarioClassLabel}
          activeLiveSession={activeLiveSession}
          showLiveButton={activeTab === 'home'}
          onScenarioChange={(value) => {
            setPreviewScenario(value)
            try {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(PREVIEW_SCENARIO_STORAGE_KEY, value)
              }
            } catch (_) {}
          }}
          onHorizonsChange={(value) => {
            setPreviewHorizonsOverride(value)
            try {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(PREVIEW_HORIZONS_STORAGE_KEY, value)
              }
            } catch (_) {}
          }}
          onClassChange={(value) => {
            setPreviewClassId(value)
            try {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(PREVIEW_CLASS_STORAGE_KEY, value)
              }
            } catch (_) {}
          }}
          onJoinLive={joinActiveLiveSession}
          onBack={() => navigate('/dashboard')}
        />
      )}
      
      {/* Header enrichi habituel */}
      <header
          className={`student-header student-header--${activeTab}`}
        >
          <div className="student-header-content">
            <div className="student-header-left">
              <button
                type="button"
                className="student-header-brand"
                onClick={() => {
                  setActiveTab('home')
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                aria-label="Retour à l'accueil"
              >
                Opus Lab
              </button>
              {isGuest && (
                <span className="student-header-guest-badge">Mode invité</span>
              )}
            </div>
            {isGuest && (
              <div className="student-header-center">
                <button
                  type="button"
                  className="student-header-cta"
                  onClick={handleHeaderCtaClick}
                  aria-label="Commencer le parcours"
                >
                  Commencer une séance
                </button>
              </div>
            )}
            <div className="student-header-right">
              {!isGuest && (
                <div className="student-header-streak-wrap" ref={streakWrapRef}>
                  <button
                    type="button"
                    className={`student-header-pill student-header-pill--streak student-header-streak-btn ${streak >= 30 ? 'student-header-pill--streak-high' : streak >= 7 ? 'student-header-pill--streak-mid' : ''} ${streakAnimate ? 'student-header-pill--streak-animate' : ''} ${streakTooltipOpen ? 'student-header-streak-btn--open' : ''}`}
                    onClick={() => setStreakTooltipOpen((open) => !open)}
                    aria-expanded={streakTooltipOpen}
                    aria-controls="student-header-streak-tooltip"
                    aria-label={`Série d'entraînement : ${streak} jour${streak !== 1 ? 's' : ''}${
                      bestStreakEverDisplay > 0 ? `, record ${bestStreakEverDisplay}` : ''
                    }. Cliquer pour les détails.`}
                  >
                    <span className="student-header-pill-icon student-header-pill-icon--flame" aria-hidden="true">🔥</span>
                    <span>{streak}</span>
                  </button>
                  {streakTooltipOpen && (
                    <div
                      id="student-header-streak-tooltip"
                      className="student-header-streak-tooltip"
                      role="tooltip"
                    >
                      <p className="student-header-streak-tooltip-title">Série d&apos;entraînement</p>
                      <p className="student-header-streak-tooltip-desc">
                        Un jour compte si tu complètes au moins un exercice.
                      </p>
                      <p className="student-header-streak-tooltip-stat">
                        {streak > 0
                          ? `${streak} jour${streak !== 1 ? 's' : ''} d'affilée`
                          : 'Aucune série en cours'}
                        {bestStreakEverDisplay > 0
                          ? ` · Record : ${bestStreakEverDisplay} jour${bestStreakEverDisplay !== 1 ? 's' : ''}`
                          : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}
            <button
              className="student-avatar-btn"
              onClick={() => {
                if (activeTab !== 'profile') setPreviousTab(activeTab)
                setActiveTab('profile')
              }}
              aria-label="Profil"
            >
              {!isGuest && (userData?.photoURL || user?.photoURL) ? (
                <img 
                  src={userData?.photoURL || user?.photoURL} 
                  alt={userName} 
                  className="student-avatar"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="student-avatar-placeholder">
                  {firstName[0]?.toUpperCase() || 'E'}
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="student-main">
        <>
        {activeTab === 'home' && (
          <div className="student-content">
            {/* Bloc pédagogique : prochaine étape + conseil du jour + CTA */}
            {!isGuest && (
              DISABLE_DAILY_LEARNING_BLOCK ? (
                <div className="dashboard-card" role="status">
                  <p>Conseil du jour temporairement indisponible.</p>
                </div>
              ) : (
                <DailyLearningBlock
                  userId={previewScenario ? undefined : user?.uid}
                  attempts={allAttemptsForStreak || []}
                  previewProgress={previewScenario ? getPreviewProgress(previewScenario) : null}
                  onContinueClick={handleHeaderCtaClick}
                  unlockedHorizonsCount={showHorizonsUi ? horizonsUnlockedCount : 0}
                  hasSeenHorizonsPanel={hasSeenHorizonsPanel}
                  onConseilHorizonsClick={() => {
                    setActiveTabAndUrl('campaign')
                    setCampaignSubTab('horizons')
                  }}
                />
              )
            )}

            {/* Session live (affichée uniquement si une session active existe) */}
            {!isGuest && (loadingLiveSession || activeLiveSession) && (
              <div className="student-devoirs-section">
                <h3 className="student-devoirs-title">Session live en cours</h3>
                {loadingLiveSession ? (
                  <div className="student-devoirs-loading">
                    <div className="spinner" aria-hidden="true" />
                    <p>Vérification de la session live…</p>
                  </div>
                ) : activeLiveSession ? (
                  <div className="student-devoirs-item">
                    <button
                      type="button"
                      className="student-devoirs-card"
                      onClick={joinActiveLiveSession}
                    >
                      <span className="student-devoirs-card-title">
                        {(Array.isArray(activeLiveSession.exerciseQueue) && activeLiveSession.exerciseQueue[activeLiveSession.currentExerciseIndex || 0]?.exerciseTitle)
                          || 'Exercice live'}
                      </span>
                      <span className="student-devoirs-card-due">
                        {activeLiveSession.status === 'correction_released'
                          ? 'Correction disponible.'
                          : 'Exercice en cours.'}
                      </span>
                      <span className={`student-devoirs-card-status ${activeLiveSession.status === 'correction_released' ? 'done' : 'todo'}`}>
                        {activeLiveSession.status === 'correction_released' ? 'Correction dispo' : 'Session active'}
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {/* Devoirs (classe du prof ou aperçu classe en mode preview) */}
            {!isGuest && (userData?.teacherClassId || (userData?.role === 'teacher' && previewClassId)) && (
              <div className="student-devoirs-section">
                <h3 className="student-devoirs-title">
                  Devoirs
                  {userData?.role === 'teacher' && previewClassId && previewTeacherClasses.length > 0 && (() => {
                    const tc = previewTeacherClasses.find((c) => c.id === previewClassId)
                    return tc ? ` (aperçu classe : ${tc.name || tc.code || previewClassId})` : ''
                  })()}
                </h3>
                {loadingAssignments ? (
                  <div className="student-devoirs-loading">
                    <div className="spinner" aria-hidden="true" />
                    <p>Chargement des devoirs…</p>
                  </div>
                ) : homeworkRows.length === 0 ? (
                  <p className="student-devoirs-empty">Aucun devoir pour le moment.</p>
                ) : (
                  <>
                    {(homeworkCounts.new > 0 || homeworkCounts.overdue > 0) && (
                      <p className="student-devoirs-empty" style={{ marginBottom: 12 }}>
                        {homeworkCounts.new > 0 ? `${homeworkCounts.new} nouveau${homeworkCounts.new > 1 ? 'x' : ''}` : ''}
                        {homeworkCounts.new > 0 && homeworkCounts.overdue > 0 ? ' • ' : ''}
                        {homeworkCounts.overdue > 0 ? `${homeworkCounts.overdue} en retard` : ''}
                      </p>
                    )}
                    <div className="student-devoirs-filters">
                      {homeworkPrimaryFilters.map((filter) => (
                        <button
                          key={filter.key}
                          type="button"
                          className={`student-devoirs-filter-chip ${homeworkFilter === filter.key ? 'is-active' : ''}`}
                          onClick={() => {
                            setHomeworkFilter(filter.key)
                            setHomeworkListExpanded(false)
                            setHomeworkSecondaryFiltersOpen(false)
                            syncStudentDashboardUrl({ tab: 'home', hw: filter.key })
                          }}
                          aria-pressed={homeworkFilter === filter.key}
                        >
                          {filter.label} ({filter.count})
                        </button>
                      ))}
                      {shouldShowHomeworkSecondaryToggle && (
                        <button
                          type="button"
                          className={`student-devoirs-filter-chip student-devoirs-filter-chip-secondary ${homeworkSecondaryFiltersOpen ? 'is-active' : ''}`}
                          onClick={() => setHomeworkSecondaryFiltersOpen((open) => !open)}
                          aria-expanded={homeworkSecondaryFiltersOpen}
                        >
                          Plus
                        </button>
                      )}
                      {homeworkSecondaryFiltersOpen && shouldShowHomeworkSecondaryToggle && (
                        homeworkSecondaryFilters
                          .filter((filter) => filter.count > 0 || homeworkFilter === filter.key)
                          .map((filter) => (
                            <button
                              key={filter.key}
                              type="button"
                              className={`student-devoirs-filter-chip student-devoirs-filter-chip-secondary ${homeworkFilter === filter.key ? 'is-active' : ''}`}
                              onClick={() => {
                                setHomeworkFilter(filter.key)
                                setHomeworkListExpanded(false)
                                syncStudentDashboardUrl({ tab: 'home', hw: filter.key })
                              }}
                              aria-pressed={homeworkFilter === filter.key}
                            >
                              {filter.label} ({filter.count})
                            </button>
                          ))
                      )}
                    </div>
                    <p className="student-devoirs-filter-summary">
                      {filteredHomeworkRows.length} devoir{filteredHomeworkRows.length > 1 ? 's' : ''} affiché{filteredHomeworkRows.length > 1 ? 's' : ''}
                    </p>
                    {filteredHomeworkRows.length === 0 ? (
                      <p className="student-devoirs-empty">Aucun devoir dans ce filtre.</p>
                    ) : (
                      <>
                      <ul className="student-devoirs-list">
                        {(homeworkListExpanded ? filteredHomeworkRows : filteredHomeworkRows.slice(0, HOMEWORK_VISIBLE_INITIAL)).map((a) => {
                          const dueStr = a.dueDate ? a.dueDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : null
                          const statusText = a.isDone ? 'Fait' : a.isOverdue ? 'En retard' : a.isNew ? 'Nouveau' : 'À faire'
                          return (
                            <li key={a.id} className="student-devoirs-item">
                              <button
                                type="button"
                                className="student-devoirs-card"
                                onClick={() => {
                                  if (!a.targetExerciseId) return
                                  navigate(`/play/${a.targetExerciseId}`, {
                                    state: { assignmentId: a.id, returnTo: `${location.pathname}${location.search || ''}` }
                                  })
                                }}
                              >
                                <span className="student-devoirs-card-title">{a.title || a.exerciseTitle || 'Exercice'}</span>
                                {a.instructions && <span className="student-devoirs-card-due">{a.instructions}</span>}
                                {a.totalCount > 1 && (
                                  <span className="student-devoirs-card-due">
                                    Progression : {a.doneCount}/{a.totalCount} exercices
                                  </span>
                                )}
                                {dueStr && <span className="student-devoirs-card-due">Pour le {dueStr}</span>}
                                <span className={`student-devoirs-card-status ${a.isDone ? 'done' : 'todo'}`}>
                                  {statusText}
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                      {filteredHomeworkRows.length > HOMEWORK_VISIBLE_INITIAL && (
                        <div className="student-devoirs-more-wrap" style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="code-submit-btn student-devoirs-more-btn"
                            onClick={() => setHomeworkListExpanded(!homeworkListExpanded)}
                            aria-expanded={homeworkListExpanded}
                          >
                            {homeworkListExpanded
                              ? 'Réduire la liste'
                              : `Voir ${filteredHomeworkRows.length - HOMEWORK_VISIBLE_INITIAL} devoir${filteredHomeworkRows.length - HOMEWORK_VISIBLE_INITIAL > 1 ? 's' : ''} de plus`}
                          </button>
                        </div>
                      )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Derniers exercices ajoutés */}
            <div className="latest-exercises-section">
              <h3 className="latest-exercises-title">
                {useTeaserCatalogOnly ? 'Exercices vitrine (beta)' : 'Derniers exercices ajoutés'}
              </h3>
              {loadingLatestExercises ? (
                <div className="latest-exercises-loading">
                  <div className="spinner" aria-hidden="true" />
                  <p>Chargement...</p>
                </div>
              ) : latestExercises.length > 0 ? (
                <>
                  <div className="latest-exercises-grid">
                    {latestExercises.map((exercise) => {
                      const vr = latestVideoRanksMap.get(exercise.id)
                      return (
                      <ExerciseCard
                        key={exercise.id}
                        exercise={exercise}
                        allExercises={latestExercises}
                        videoRank={vr?.rank}
                        videoGroupSize={vr?.groupSize}
                        showDemoTeaserBadge={useTeaserCatalogOnly}
                        onClick={(id) => navigate(`/play/${id}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })}
                        onPillClick={(payload) => {
                          if (payload.type === 'difficulty') setFreeModeInitialFilter({ difficulty: payload.value })
                          else if (payload.type === 'tag') setFreeModeInitialFilter({ tag: payload.value })
                          else if (payload.type === 'genre') setFreeModeInitialFilter({ genre: payload.value })
                          else if (payload.type === 'formation') setFreeModeInitialFilter({ formation: payload.value })
                          setActiveTabAndUrl('free-mode')
                        }}
                        onOpenCodex={(ficheId, sectionId) => navigate(getCodexUrlForEntry(ficheId, sectionId))}
                        recommendedLabel={doneExerciseIds.includes(exercise.id) ? 'Déjà fait' : undefined}
                      />
                    )})}
                  </div>
                  <div className="latest-exercises-cta-row">
                    <button
                      type="button"
                      className="latest-exercises-cta"
                      onClick={() => setActiveTabAndUrl('free-mode')}
                    >
                      Voir tout en Mode Libre
                    </button>
                    {!isGuest && doneExerciseIds.length > 0 && (
                      <button
                        type="button"
                        className="latest-exercises-cta latest-exercises-cta-secondary"
                        onClick={() => {
                          setFreeModeInitialFilter({ doneStatus: 'done' })
                          setActiveTabAndUrl('free-mode')
                        }}
                      >
                        Voir les exercices déjà faits
                      </button>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            {/* Section Code Exercice */}
            <div className="code-section">
              <h3 className="code-title">Code Exercice</h3>
              <p className="code-subtitle">Entre le code court (ou l'identifiant) donné par ton professeur</p>
          <form onSubmit={handleCodeSubmit} className="code-form">
                <div className="code-input-wrapper">
            <input
              type="text"
              className="code-input"
                    placeholder="Ex: A7K9P2"
              value={exerciseCode}
              onChange={(e) => {
                setExerciseCode(e.target.value)
                setCodeError('')
              }}
            />
                  <button type="submit" className="code-submit-btn" aria-label="Jouer">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
            </button>
                </div>
                {codeError && <p className="code-error">{codeError}</p>}
          </form>
        </div>
          </div>
        )}

        {activeTab === 'campaign' && (
          <div className="student-content campaign-tab-content" ref={campaignMapSectionRef}>
            {/* Sous-onglets Parcours / Nouveaux Horizons */}
            {showHorizonsUi && (
              <div className="campaign-subtabs">
                <button
                  type="button"
                  className={`campaign-subtab ${campaignSubTab === 'classic' ? 'active' : ''}`}
                  onClick={() => setCampaignSubTab('classic')}
                >
                  Parcours
                </button>
                <button
                  type="button"
                  className={`campaign-subtab ${campaignSubTab === 'horizons' ? 'active' : ''}`}
                  onClick={() => setCampaignSubTab('horizons')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                  Nouveaux Horizons
                </button>
              </div>
            )}

            {campaignSubTab === 'classic' && (
              <>
                {!isGuest && user ? (
                  <div className="campaign-map-wrap">
                    {campaignSelectedNodeId ? (
                      <ParcoursNodePage
                        nodeId={campaignSelectedNodeId}
                        userId={previewScenario ? undefined : user.uid}
                        progress={null}
                        doneExerciseIds={doneExerciseIds}
                        onBack={() => setCampaignSelectedNodeId(null)}
                        onOpenCodex={(nodeId) => {
                          setCodexInitialNodeId(nodeId)
                          setActiveTabAndUrl('codex')
                          setCampaignSelectedNodeId(null)
                        }}
                        isPreviewMode={isPreviewMode}
                        previewProgress={previewScenario ? getPreviewProgress(previewScenario) : null}
                      />
                    ) : (
                      <CampaignMap
                        userId={previewScenario ? undefined : user.uid}
                        isPreviewMode={isPreviewMode}
                        previewProgress={previewScenario ? getPreviewProgress(previewScenario) : null}
                        onOpenCodex={(nodeId) => {
                          setCodexInitialNodeId(nodeId)
                          setActiveTabAndUrl('codex')
                        }}
                        onSelectNode={(nodeId) => setCampaignSelectedNodeId(nodeId)}
                        doneExerciseIds={doneExerciseIds}
                      />
                    )}
                  </div>
                ) : (
                  <div className="campaign-tab-guest">
                    <p>Connecte-toi pour accéder au parcours de progression.</p>
                    <button
                      type="button"
                      className="latest-exercises-cta"
                      onClick={() => navigate('/')}
                    >
                      Se connecter
                    </button>
                  </div>
                )}
              </>
            )}

            {campaignSubTab === 'horizons' && showHorizonsUi && (
              <div className="student-horizons-main">
                <HorizonsMap
                  attempts={allAttemptsForStreak || []}
                  userXp={userData?.xp ?? 0}
                  doneExerciseIds={doneExerciseIds}
                  onSwitchTab={setActiveTab}
                  onClose={() => setCampaignSubTab('classic')}
                  previewUnlockedHorizonsStyles={
                    isPreviewMode && previewHorizonsOverride
                      ? horizonsUnlockedStyles
                      : null
                  }
                  selectedStyleId={selectedHorizonsStyleId}
                  setSelectedStyleId={setSelectedHorizonsStyleId}
                  hideInternalBack
                  publicTeaserOnly={useTeaserCatalogOnly}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="student-content">
            <div className="progression-section">
              <h2 className="section-title">Suivi</h2>

              {showHorizonsUnlockBanner && (
                <div className="suivi-horizons-unlock" role="status">
                  <div className="suivi-horizons-unlock-text">
                    <strong>Nouveaux Horizons débloqué.</strong>
                    {' '}Explore des styles hors parcours classique.
                  </div>
                  <div className="suivi-horizons-unlock-actions">
                    <button
                      type="button"
                      className="suivi-horizons-unlock-cta"
                      onClick={() => {
                        dismissHorizonsUnlockBanner()
                        setActiveTabAndUrl('campaign')
                        setCampaignSubTab('horizons')
                      }}
                    >
                      Ouvrir
                    </button>
                    <button
                      type="button"
                      className="suivi-horizons-unlock-dismiss"
                      onClick={dismissHorizonsUnlockBanner}
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              {loadingAttempts || loadingParcoursProgress || loadingProfileStats ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : attempts.length === 0 ? (
                <>
                  <div className="progress-section-card">
                    <DiagnosticHero
                      {...diagnosticCopy}
                      onPrimary={handleDiagnosticPrimary}
                      onSecondary={handleDiagnosticSecondary}
                      onWorkPriority={handleWorkWeakSkill}
                      onOpenCodex={(ficheId, sectionId) =>
                        navigate(getCodexUrlForEntry(ficheId, sectionId))
                      }
                    />
                  </div>
                  <div className="progress-section-card">
                    <ParcoursMasteryCard
                      summary={parcoursSummary}
                      isGuest={isGuest}
                      onOpenParcours={() => {
                        setActiveTabAndUrl('campaign')
                        setCampaignSubTab('classic')
                      }}
                      onConnect={() => setActiveTab('profile')}
                    />
                  </div>
                  {!isGuest && pendingHomeworkCount > 0 ? (
                    <div className="progress-section-card">
                      <HomeworkPlan rows={homeworkRows} />
                    </div>
                  ) : null}
                  <div className="empty-state">
                    <h3 className="empty-title">Aucun exercice complété</h3>
                    <p className="empty-text">
                      Lance une séance pour obtenir un bilan de compétences et tes objectifs de la
                      semaine.
                    </p>
                    <button
                      type="button"
                      className="empty-cta"
                      onClick={() => setActiveTabAndUrl('home')}
                    >
                      Commencer une séance
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="progress-section-card">
                    <DiagnosticHero
                      {...diagnosticCopy}
                      onPrimary={handleDiagnosticPrimary}
                      onSecondary={handleDiagnosticSecondary}
                      onWorkPriority={handleWorkWeakSkill}
                      onOpenCodex={(ficheId, sectionId) =>
                        navigate(getCodexUrlForEntry(ficheId, sectionId))
                      }
                    />
                  </div>

                  <div className="progress-section-card">
                    <ParcoursMasteryCard
                      summary={parcoursSummary}
                      isGuest={isGuest}
                      onOpenParcours={() => {
                        setActiveTabAndUrl('campaign')
                        setCampaignSubTab('classic')
                      }}
                      onConnect={() => setActiveTab('profile')}
                    />
                  </div>

                  <div className="progress-section-card">
                    <CompetencesSection
                      attempts={allAttemptsForStreak}
                      profileStats={profileStats}
                      radarData={radarData}
                    />
                  </div>

                  <div className="progress-section-card">
                    <WeeklyActivityStrip attempts={allAttemptsForStreak} />
                  </div>

                  {!isGuest && pendingHomeworkCount > 0 ? (
                    <div className="progress-section-card">
                      <HomeworkPlan rows={homeworkRows} />
                    </div>
                  ) : null}

                  <div className="progress-section-card" ref={nextPracticeRef}>
                    {!isGuest ? (
                      <PracticePlan
                        profileStats={profileStats}
                        parcoursSummary={parcoursSummary}
                        attempts={allAttemptsForStreak}
                        availableTags={tagsForWorkAdvice}
                        onGoParcours={() => {
                          setActiveTabAndUrl('campaign')
                          setCampaignSubTab('classic')
                        }}
                        onWorkSkill={handleWorkWeakSkill}
                        onOpenCodex={(ficheId, sectionId) =>
                          navigate(getCodexUrlForEntry(ficheId, sectionId))
                        }
                        onAllComplete={setAllObjectivesCompleted}
                      />
                    ) : null}
                    <div className={!isGuest ? 'suivi-suggestions-wrap' : undefined}>
                      <ExerciseSuggestions
                        profileStats={profileStats}
                        attempts={allAttemptsForStreak}
                        publicTeaserOnly={useTeaserCatalogOnly}
                        title="Exercices recommandés"
                        subtitle={formatSuggestionsSubtitle(weakSkillsList[0])}
                        onSwitchTab={setActiveTab}
                        onPillClick={(payload) => {
                          if (payload.type === 'tag') setFreeModeInitialFilter({ tag: payload.value })
                          else if (payload.type === 'difficulty') setFreeModeInitialFilter({ difficulty: payload.value })
                          else if (payload.type === 'doneStatus') setFreeModeInitialFilter({ doneStatus: payload.value })
                          else if (payload.type === 'genre') setFreeModeInitialFilter({ genre: payload.value })
                          else if (payload.type === 'formation') setFreeModeInitialFilter({ formation: payload.value })
                          setActiveTabAndUrl('free-mode')
                        }}
                        onOpenCodex={(ficheId, sectionId) => navigate(getCodexUrlForEntry(ficheId, sectionId))}
                        allObjectivesCompleted={allObjectivesCompleted}
                      />
                    </div>
                  </div>

                  <div className="progress-section-card">
                    <h3 className="progress-section-title">Journal</h3>
                    <div className="attempts-list attempts-list--compact">
                      {journalGroups
                        .slice(0, historyExpanded ? PROGRESS_HISTORY_LIST_LIMIT : PROGRESS_HISTORY_LIST_VISIBLE)
                        .map((group) => {
                          const groupKey = group.exerciseId || group.attempts[0]?.id
                          const isExpanded = expandedJournalKey === groupKey
                          return (
                            <div key={groupKey} className="attempt-journal-group">
                              <div
                                role="button"
                                tabIndex={0}
                                className="attempt-row attempt-row-clickable"
                                onClick={() => {
                                  if (group.attemptCount > 1) {
                                    setExpandedJournalKey(isExpanded ? null : groupKey)
                                  } else {
                                    setSelectedAttempt(group.bestAttempt)
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter') return
                                  if (group.attemptCount > 1) {
                                    setExpandedJournalKey(isExpanded ? null : groupKey)
                                  } else {
                                    setSelectedAttempt(group.bestAttempt)
                                  }
                                }}
                                aria-label={`${cleanExerciseTitle(group.exerciseTitle)} — ${group.bestScore}%`}
                              >
                                <div className="attempt-row-main">
                                  <span className="attempt-row-title" title={cleanExerciseTitle(group.exerciseTitle)}>
                                    {cleanExerciseTitle(group.exerciseTitle)}
                                  </span>
                                  <span className="attempt-row-meta">
                                    {formatDate(group.lastCompletedAt)}
                                    {group.attemptCount > 1 ? ` · ${group.attemptCount} tentatives` : ''}
                                    {' · '}
                                    Record {group.bestScore}%
                                  </span>
                                </div>
                                <span className={`attempt-row-score attempt-score-${group.bestScore >= 80 ? 'high' : group.bestScore >= 60 ? 'medium' : 'low'}`}>
                                  {group.bestScore}%
                                </span>
                              </div>
                              {isExpanded && group.attemptCount > 1 ? (
                                <div className="attempt-journal-group-details">
                                  {group.attempts.map((attempt) => (
                                    <div
                                      key={attempt.id}
                                      role="button"
                                      tabIndex={0}
                                      className="attempt-row attempt-row-clickable attempt-row--nested"
                                      onClick={() => setSelectedAttempt(attempt)}
                                      onKeyDown={(e) => e.key === 'Enter' && setSelectedAttempt(attempt)}
                                    >
                                      <div className="attempt-row-main">
                                        <span className="attempt-row-meta">
                                          {formatDate(attempt.completedAt)}
                                          {' · '}
                                          {attempt.correctCount}/{attempt.totalQuestions} justes
                                          {attempt.score != null ? ` · ${attempt.score}%` : ''}
                                          {attempt.id === group.bestAttempt?.id ? ' · Record' : ''}
                                        </span>
                                      </div>
                                      <span className={`attempt-row-score attempt-score-${attempt.score >= 80 ? 'high' : attempt.score >= 60 ? 'medium' : 'low'}`}>
                                        {attempt.score}%
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          )
                        })}
                    </div>
                    {!historyExpanded && journalGroups.length > PROGRESS_HISTORY_LIST_VISIBLE && (
                      <button
                        type="button"
                        className="progress-history-see-more"
                        onClick={() => setHistoryExpanded(true)}
                        aria-label={`Afficher plus d’entrées`}
                      >
                        Voir plus
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'codex' && (
          <div className="student-content codex-tab-content">
            {codexLoading ? (
              <div className="dashboard-loading"><div className="spinner"></div><p>Chargement du Codex...</p></div>
            ) : codexPublishedEntries.length === 0 ? (
              <CodexPlaceholder />
            ) : (
              <CodexView
                entries={codexPublishedEntries}
                selectedEntryId={codexSelectedFicheId}
                onSelectEntry={setCodexSelectedFicheId}
                initialFicheId={codexInitialFicheId}
                initialNodeId={codexInitialNodeId}
                initialSectionId={codexInitialSectionId}
                onOpenParcoursNode={() => {
                  setActiveTabAndUrl('campaign')
                  setCodexSelectedFicheId(null)
                }}
                onOpenFreeModeWithTag={(tag) => {
                  setFreeModeInitialFilter(tag ? { tag } : null)
                  setActiveTabAndUrl('free-mode')
                  setCodexSelectedFicheId(null)
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'free-mode' && (
          <FreeMode
            doneExerciseIds={doneExerciseIds}
            initialFilter={freeModeInitialFilter}
            onInitialFilterConsumed={() => setFreeModeInitialFilter(null)}
            horizonsUnlocked={showHorizonsUi}
            onOpenCodex={(ficheId, sectionId) => navigate(getCodexUrlForEntry(ficheId, sectionId))}
            publicTeaserOnly={useTeaserCatalogOnly}
          />
        )}

        {activeTab === 'profile' && (
          <div className="student-content profile-page">
            {isGuest ? (
              <div className="guest-connect-card">
                <div className="guest-illustration">💾</div>
                <h2 className="guest-title">Sauvegarder ta progression</h2>
                <p className="guest-text">
                  Connecte-toi avec Google pour sauvegarder tes scores et suivre ta progression.
                </p>
                <button
                  className="guest-connect-btn"
                  disabled={isSigningInFromGuest || authLoading}
                  onClick={async () => {
                    try {
                      setIsSigningInFromGuest(true)
                      await signInWithGoogle()
                      // La redirection est gérée par AppRouter selon le rôle
                    } catch (error) {
                      console.error('Erreur lors de la connexion:', error)
                      if (error?.code === 'auth/popup-closed-by-user') return
                      const message = getAuthErrorMessage(error)
                      alert(message)
                    } finally {
                      setIsSigningInFromGuest(false)
                    }
                  }}
                >
                  {isSigningInFromGuest || authLoading ? (
                    <>
                      <span className="spinner-small"></span>
                      Connexion...
                    </>
                  ) : (
                    'Se connecter avec Google'
                  )}
                </button>
                <button
                  className="guest-exit-btn"
                  onClick={() => {
                    disableGuestMode()
                    navigate('/')
                  }}
                >
                  Quitter le mode invité
                </button>
              </div>
            ) : loadingProfileStats ? (
              <div className="profile-loading">
                <div className="spinner"></div>
                <p>Chargement des statistiques...</p>
              </div>
            ) : (
              <>
                {/* Barre retour (Profil accessible uniquement via avatar) */}
                <div className="profile-top-bar">
                  <button
                    type="button"
                    className="profile-back-btn"
                    onClick={() => setActiveTab(previousTab || 'home')}
                    aria-label="Retour"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                  </button>
                  <h1 className="profile-top-bar-title">Profil</h1>
                </div>

                {/* Section 1: Header & Global (Gamification) — sans grosses stats */}
                <div className="profile-header-section">
                  <div className="profile-header-gamification">
                    <div className="profile-inline-avatar-edit">
                      <div className="profile-avatar-container">
                        {profilePhotoURL ? (
                          <img 
                            src={profilePhotoURL} 
                            alt={userName} 
                            className="profile-page-avatar"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="profile-page-avatar-placeholder">
                            {firstName[0]?.toUpperCase() || 'E'}
                          </div>
                        )}
                      </div>
                      {!isGuest && user?.uid && (
                        <button
                          type="button"
                          className="profile-page-change-avatar-btn"
                          onClick={() => setAvatarPickerOpen(true)}
                        >
                          Changer l’image de profil
                        </button>
                      )}
                    </div>
                    <div className="profile-header-info">
                      <div className="profile-inline-name-edit">
                        {editingDisplayName ? (
                          <>
                            <input
                              type="text"
                              className="profile-inline-name-input"
                              value={displayNameInput}
                              onChange={(e) => setDisplayNameInput(e.target.value)}
                              placeholder="Votre nom"
                              disabled={displayNameSaving}
                              aria-label="Nom d'affichage"
                            />
                            <div className="profile-inline-name-actions">
                              <button
                                type="button"
                                className="profile-inline-name-save"
                                onClick={saveDisplayName}
                                disabled={displayNameSaving}
                              >
                                {displayNameSaving ? 'Enregistrement…' : 'Enregistrer'}
                              </button>
                              <button
                                type="button"
                                className="profile-inline-name-cancel"
                                onClick={cancelEditingDisplayName}
                                disabled={displayNameSaving}
                              >
                                Annuler
                              </button>
                            </div>
                            {displayNameError && (
                              <p className="profile-inline-name-error" role="alert">{displayNameError}</p>
                            )}
                          </>
                        ) : (
                          <>
                            <h2 className="profile-page-name">{userName}</h2>
                            {!isGuest && user?.uid && (
                              <button
                                type="button"
                                className="profile-inline-name-edit-btn"
                                onClick={startEditingDisplayName}
                              >
                                Modifier le nom
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="profile-header-stats">
                        <div className="profile-stat-item">
                          <span className="profile-stat-label streak-label">
                            <span className="streak-icon">🔥</span>
                            Série
                          </span>
                          <span className="profile-stat-value">
                            {profileStats?.streak || 0} jour{profileStats?.streak !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Établissement et classe */}
                <div className="profile-section-card profile-establishment-section">
                  <h3 className="profile-section-title">Établissement et classe</h3>
                  <p className="profile-establishment-hint">
                    Ces informations permettent à ton professeur de filtrer les élèves par niveau.
                    {userData?.teacherClassId
                      ? ' Tant que tu es dans une classe (code prof), elles viennent de cette classe ; quitte-la pour les modifier à la main.'
                      : ''}
                  </p>
                  <div className="profile-establishment-fields">
                    <div className="profile-establishment-field">
                      <label htmlFor="profile-establishment-select">Établissement</label>
                      <select
                        id="profile-establishment-select"
                        value={profileEstablishment}
                        onChange={(e) => {
                          const v = e.target.value
                          if (v === 'Autre') {
                            setRequestModalType('establishment')
                            setRequestModalOpen(true)
                            setProfileEstablishment(profileEstablishment)
                            return
                          }
                          setProfileEstablishment(v)
                          if (user?.uid) updateUserProfile(user.uid, { establishment: v || null }).catch(console.error)
                        }}
                        disabled={loadingEstablishments || !!userData?.teacherClassId}
                      >
                        <option value="">— Choisir —</option>
                        {establishmentsList.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="profile-establishment-field">
                      <label htmlFor="profile-class-select">Classe</label>
                      <select
                        id="profile-class-select"
                        value={profileClass}
                        onChange={(e) => {
                          const v = e.target.value
                          if (v === 'Autre') {
                            setRequestModalType('class')
                            setRequestModalOpen(true)
                            setProfileClass(profileClass)
                            return
                          }
                          setProfileClass(v)
                          if (user?.uid) updateUserProfile(user.uid, { class: v || null }).catch(console.error)
                        }}
                        disabled={loadingClasses || !!userData?.teacherClassId}
                      >
                        <option value="">— Choisir —</option>
                        {profileClass && !classesList.includes(profileClass) ? (
                          <option value={profileClass}>{profileClass}</option>
                        ) : null}
                        {classesList.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Rejoindre une classe (code donné par le professeur) */}
                <div className="profile-section-card profile-teacher-class-section">
                  <h3 className="profile-section-title">Ma classe</h3>
                  {userData?.teacherClassId && teacherClassName ? (
                    <>
                      <p className="profile-teacher-class-joined">
                        Vous êtes dans la classe : <strong>{teacherClassName}</strong>
                      </p>
                      <button
                        type="button"
                        className="profile-teacher-class-leave"
                        onClick={async () => {
                          if (!user?.uid) return
                          setJoinClassLoading(true)
                          setJoinClassError('')
                          try {
                            await leaveTeacherClass(user.uid)
                            await refreshUserData()
                            setTeacherClassName(null)
                          } catch (err) {
                            setJoinClassError(err.message || 'Erreur')
                          } finally {
                            setJoinClassLoading(false)
                          }
                        }}
                        disabled={joinClassLoading}
                      >
                        {joinClassLoading ? '…' : 'Quitter la classe'}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="profile-teacher-class-hint">
                        Entre le code donné par ton professeur pour rejoindre sa classe.
                      </p>
                      <div className="profile-teacher-class-join-row">
                        <input
                          type="text"
                          className="profile-teacher-class-code-input"
                          placeholder="Code classe"
                          value={classCodeInput}
                          onChange={(e) => {
                            setClassCodeInput(e.target.value.toUpperCase().trim())
                            setJoinClassError('')
                          }}
                          disabled={joinClassLoading}
                        />
                        <button
                          type="button"
                          className="profile-teacher-class-join-btn"
                          onClick={async () => {
                            const code = classCodeInput.trim()
                            if (!code || !user?.uid) return
                            setJoinClassLoading(true)
                            setJoinClassError('')
                            try {
                              await joinTeacherClass(user.uid, code)
                              await refreshUserData()
                              setClassCodeInput('')
                            } catch (err) {
                              setJoinClassError(err.message || 'Code invalide.')
                            } finally {
                              setJoinClassLoading(false)
                            }
                          }}
                          disabled={joinClassLoading || !classCodeInput.trim()}
                        >
                          {joinClassLoading ? '…' : 'Rejoindre'}
                        </button>
                      </div>
                      {joinClassError && <p className="profile-teacher-class-error">{joinClassError}</p>}
                    </>
                  )}
                </div>

                {/* Heatmap d'activité */}
                {allAttempts.length > 0 && (
                  <div className="profile-section-card">
                    <h3 className="profile-section-title">Historique d&apos;activité</h3>
                    <ActivityHeatmap attempts={allAttempts} />
                  </div>
                )}

                {/* Accomplissements (replié — hors flux Suivi) */}
                <details className="profile-section-card profile-achievements-details">
                  <summary className="profile-section-title profile-achievements-summary">
                    Accomplissements
                  </summary>
                  <AchievementsDashboard
                    userId={user?.uid}
                    attempts={allAttemptsForStreak}
                    userXp={userData?.xp}
                    onOpenHorizons={
                      showHorizonsUi
                        ? () => {
                            setActiveTabAndUrl('campaign')
                            setCampaignSubTab('horizons')
                          }
                        : null
                    }
                  />
                </details>

                {/* Bouton Déconnexion */}
                <button
                  className="profile-logout-btn"
                  onClick={async () => {
                    try {
                      await logout()
                      navigate('/')
                    } catch (error) {
                      console.error('Erreur lors de la déconnexion:', error)
                    }
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Déconnexion
                </button>
              </>
            )}
          </div>
        )}
        </>
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <button
          className={`nav-item ${activeTab === 'home' ? 'nav-item-active' : ''}`}
          onClick={() => setActiveTabAndUrl('home')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span>Accueil</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'campaign' ? 'nav-item-active' : ''}`}
          onClick={() => setActiveTabAndUrl('campaign')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
          <span>Parcours</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'progress' ? 'nav-item-active' : ''}`}
          onClick={() => setActiveTabAndUrl('progress')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
          <span>Suivi</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'free-mode' ? 'nav-item-active' : ''}`}
          onClick={() => setActiveTabAndUrl('free-mode')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <span>Mode Libre</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'codex' ? 'nav-item-active' : ''}`}
          onClick={() => setActiveTabAndUrl('codex')}
          aria-label="Codex"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2"></path>
            <path d="M8 7h8"></path>
            <path d="M8 11h8"></path>
          </svg>
          <span>Codex</span>
        </button>
      </nav>

      {selectedAttempt && (
        <AttemptDetailModal
          attempt={selectedAttempt}
          exerciseTitle={cleanExerciseTitle(selectedAttempt.exerciseTitle)}
          onClose={() => setSelectedAttempt(null)}
        />
      )}

      <RequestEstablishmentModal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        type={requestModalType}
        userId={user?.uid}
      />

      {!isGuest && (
        <AvatarPickerModal
          isOpen={avatarPickerOpen}
          onClose={() => setAvatarPickerOpen(false)}
          user={user}
          userData={userData}
          isAdminTeacher={isAdminTeacher}
        />
      )}
    </div>
  )
}

export default StudentDashboard
